import React, { PropsWithChildren, ReactNode, useContext, useMemo, useRef, useState } from 'react'
import styled from '~/app/components/styled'
import { zIndexMiddle } from '~/app/components/styles/units'
import useEventHooks from '~/app/components/common/useEventHooks'
import usePreventDoubleEvent from '~/app/utils/usePreventDoubleEvent'
import { GlobalContext } from '~/app/components/common/GlobalProvider'
import { sendKinesisClickLogTimeoutAfter } from '~/app/utils/timeout'
import { Icon_arrowback, Icon_close } from '@yanolja-rnd/ya-com-system-icon'
import { Typography } from '@yanolja-rnd/ya-com-core'

/**
 * Toolbar component
 * author: soojin.oh
 */

/**
 * 툴바의 내용물의 높이 맞춰 자신의 높이를 재 계산하여 적용하기 떄문에 리플로우가 일어나게 된다.
 * 보통의 컨텐츠라면 상관이 없겠으나 2년치 목록의 캘린더 등과 같이 사용하게 되면 리플로우되는 돔요소의 양이 엄청나기 때문에 많은 시간을 소비하게 된다.
 * 고정된 height 값을 설정해주면 높이의 재 적용이 생략되어 성능 향상을 불러올 수 있다.
 */

// type definitions
type Props = {
  title: string
  rightAppended?: ReactNode
  textAlign?: 'left' | 'center'
  position?: 'fixed' | 'absolute'
  type?: 'back' | 'close'
  onClickBackButton?: (e: React.MouseEvent) => void
  fixedHeight?: number
  hasShadow?: boolean
  borderless?: boolean
  sendIconClickLog?: () => void
}

// styled components
const ToolbarContainer = styled.div<{ height: number }>`
  width: 100%; // vw 로 설명할 경우 스크롤영역을 침범하게 되어 횡스크롤 생성됨
  height: ${({ height }) => height / 10}rem;
  position: relative;
`
const Background = styled.div<{ position: string }>`
  position: ${p => p.position};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  box-sizing: border-box;
  border-bottom: solid 0.1rem ${({ theme }) => theme.color.gray200};
  background: ${({ theme }) => theme.color.white};
  width: 100%;
  z-index: ${zIndexMiddle};

  &.borderless {
    border-bottom: none;
  }
`

const TitleBar = styled.div`
  width: 100%;
  height: 100%;
  line-height: 2.7rem;
`
const Title = styled.div<{ defaultHeight: number | null }>`
  display: grid;
  padding: 1rem 2rem;
  grid-template-columns: 2.4rem 1fr 2.4rem;
  gap: 0.8rem;
  align-items: center;
  ${Typography('subtitle2').bold};
  color: ${({ theme }) => theme.color.gray900};
  max-width: ${({ theme }) => theme.unit.PC_WIDTH}px;
  margin: 0 auto;

  ${({ defaultHeight }) => defaultHeight && `height: ${defaultHeight}px;`}
`
const TitleText = styled.div`
  width: 100%;
  text-align: left;

  &.center {
    text-align: center;
  }
`
const ButtonWrap = styled.div`
  cursor: pointer;
  display: flex;
  height: 100%;
`

const FixedContainer = styled.div<{ height: number }>`
  position: fixed;
  height: ${({ height }) => height}px;
  width: 100%;
  z-index: 2; // swiper가 1임

  :after {
    content: ' ';
    width: 100%;
    height: 0.1rem;
    position: absolute;
    bottom: 0;
    box-shadow: 0 0 0.6rem 0 rgba(0, 0, 0, 0.3);
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &.shadow {
    :after {
      opacity: 1;
    }
  }
`

const leftToolbarMobiles: (string | null)[] = ['Android', 'WindowsPhone']

// Toolbar component
const Toolbar = function ({
  title,
  textAlign,
  position = 'absolute',
  children,
  rightAppended,
  type = 'back',
  onClickBackButton,
  fixedHeight = 0,
  hasShadow = false,
  borderless = true,
  sendIconClickLog,
}: PropsWithChildren<Props>) {
  // toolbar(48px) 의 default height value for SSR
  const defaultHeight = useRef(fixedHeight || 48).current
  const [containerHeight, setContainerHeight] = useState(defaultHeight)

  const { device } = useContext(GlobalContext)
  const align = useMemo(
    () => (textAlign || leftToolbarMobiles.includes(device) ? 'left' : 'center'),
    [textAlign, device]
  )

  const { stop } = useEventHooks()
  const onClickBackButtonAndStop = useMemo(
    () =>
      stop(async (e: React.MouseEvent) => {
        if (sendIconClickLog) {
          await sendKinesisClickLogTimeoutAfter(sendIconClickLog)
        }
        onClickBackButton ? onClickBackButton(e) : window.history.back()
      }),
    [onClickBackButton]
  )
  const [onBackClick] = usePreventDoubleEvent(onClickBackButtonAndStop)

  return (
    <ToolbarContainer height={containerHeight}>
      <FixedContainer height={containerHeight} className={hasShadow ? 'shadow' : ''}>
        <Background
          position={position}
          ref={target => fixedHeight == 0 && setContainerHeight(target?.offsetHeight || 0)}
          className={borderless ? 'borderless' : ''}
          id={'background'}
        >
          <TitleBar>
            <Title defaultHeight={!title ? defaultHeight : null}>
              <ButtonWrap onClick={onBackClick}>
                {type === 'back' ? (
                  <Icon_arrowback size={2.4} unit="rem" />
                ) : (
                  <Icon_close size={2.4} unit="rem" />
                )}
              </ButtonWrap>
              <TitleText className={align}>{title}</TitleText>
              {rightAppended && <ButtonWrap>{rightAppended}</ButtonWrap>}
            </Title>
            {children && <div>{children}</div>}
          </TitleBar>
        </Background>
      </FixedContainer>
    </ToolbarContainer>
  )
}

export default Toolbar
